<template>
  <aside
    class="bg-white border-r border-gray-300 flex flex-col h-full flex-shrink-0 transition-all duration-300 ease-in-out overflow-hidden"
    :class="isCollapsed ? 'w-16' : 'w-60'"
    @mouseenter="toggleCollapseManual(true)"
    @mouseleave="toggleCollapseManual(false)"
  >
    <div
      class="p-3 border-b border-gray-300 flex items-center justify-between self-center"
      :class="isCollapsed? ' justify-center': ''"
    >
      <div
        v-if="!isCollapsed"
        class="flex items-center  hover:rounded-lg cursor-pointer p-2 relative"
        :class="isCollapsed ? 'w-full justify-center' : 'w-full'"
      >
        <img
          v-if="workSpace?.logo"
          :src="workSpace.logo"
          :alt="workSpace.name"
          class="w-9 h-9  object-contain rounded-lg"
          :class="isCollapsed ? 'mr-0' : 'mr-3'"
        >
        <div
          class="font-bold text-lg text-gray-900"
        >
          {{ workSpace?.name }}
        </div>
        <div
          v-if="workSpaces.length > 1 && !isCollapsed"
          class="right-0 absolute"
        />
      </div>
      <button
        v-if="!isKeepSidebarOpen"
        class="p-1 hover:bg-gray-100 rounded-lg transition-colors"
        @click="toggleCollapse"
      >
        <Icon
          :name="isCollapsed ? 'mdi-chevron-right' : 'mdi-chevron-left'"
          class="w-5 h-5 text-gray-500"
        />
      </button>
    </div>

    <div
      v-if="!isAuditor && !isCollapsed"
      class="p-3 border-b border-gray-300"
    >
      <Button
        outlined
        class="w-full"
        @click="openSearch"
      >
        <div class="flex items-center justify-between w-full">
          <span class="flex items-center gap-2">
            <Icon
              name="lucide:search"
              class="w-4 h-4 text-gray-500 flex-shrink-0"
            />
            <span>Search</span>
          </span>
          <kbd class="hidden sm:inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold text-gray-500 bg-gray-100 border border-gray-300 rounded">
            <span>⌘</span>
            <span>+</span>
            <span>K</span>
          </kbd>
        </div>
      </Button>
    </div>
    <div
      v-else-if="!isAuditor"
      class="p-2 border-b border-gray-300 flex justify-center"
    >
      <Button
        icon="pi pi-search"
        text
        @click="openSearch"
      />
    </div>
    <div class="flex-1 p-3 overflow-y-auto">
      <template
        v-for="(menuGroup, groupIndex) in menus"
        :key="groupIndex"
      >
        <div
          v-if="menuGroup.name && !isCollapsed"
          class="text-xs font-semibold text-gray-500 mb-2 mt-4"
        >
          {{ menuGroup.name }}
        </div>
        <div
          v-if="menuGroup.name && isCollapsed"
          :class="isCollapsed ? 'border-b border-gray-300 mb-2' : ''"
        />
        <div
          v-for="(menu, index) in menuGroup.menus"
          :key="menu.to || index"
          class="flex items-center p-2 rounded-lg text-gray-600 text mb-1 cursor-pointer transition font-medium"
          :class="[
            isActive(menu) ? 'bg-primary-100 text-primary-700 rounded hover:bg-primary-200' : 'hover:bg-gray-100',
            isCollapsed ? 'justify-center' : '',
          ]"
          @click="navigateTo(menu)"
        >
          <Icon
            v-if="menu.icon"
            :name="menu.icon"
            class="flex-shrink-0"
            :class="isCollapsed ? 'w-5 h-5' : 'w-4 mr-3'"
          />
          <span v-if="!isCollapsed">{{ menu.name }}</span>
        </div>
      </template>
    </div>
    <div class="p-2 border-t border-gray-200">
      <Button
        v-if="!isAuditor"
        outlined
        class="w-full mb-2 hover:bg-primary-50 transition-colors"
        severity="secondary"
        @click="openAskAi"
      >
        <Icon
          name="mdi-message-text-outline"
          class="mr-2"
        />
        <span v-if="!isCollapsed">Ask CIM-PRO</span>
      </Button>
      <Button
        outlined
        class="w-full mb-3 hover:bg-primary-50 transition-colors"
        severity="secondary"
        onclick="Supademo.widget.open('cmmjzq8f00081ud0jjst1u5g0')"
      >
        <Icon
          name="mdi-play-circle-outline"
          class="mr-2"
        />
        <span v-if="!isCollapsed">Training Videos</span>
      </Button>
    </div>
    <div
      @click="toggleMenu"
    >
      <div
        v-if="checkIfSupport"
        class="p-4 border-t border-gray-200 flex items-center cursor-pointer hover:bg-gray-50 transition-colors"
        :class="isCollapsed ? 'justify-center' : ''"
      >
        <div
          class="w-10 h-10 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl flex items-center justify-center text-white shadow-sm"
          :class="isCollapsed ? 'mr-0' : 'mr-3'"
        >
          <Icon name="mdi-support" />
        </div>
        <div
          v-if="!isCollapsed"
          class="flex-1"
        >
          <div class="font-medium text-sm text-gray-900">
            Support
          </div>
        </div>
      </div>

      <div
        v-else
        class="p-4 border-t border-gray-300 flex items-center cursor-pointer"
        :class="isCollapsed ? 'justify-center' : ''"
      >
        <div
          class="w-9 h-9 bg-gray-300 rounded-lg flex items-center justify-center font-semibold text-gray-600 text-sm"
          :class="isCollapsed ? 'mr-0' : 'mr-3'"
        >
          {{ getInitials() }}
        </div>
        <div
          v-if="!isCollapsed"
          class="flex-1"
        >
          <div class="font-medium text-sm text-gray-900">
            {{ activeUser?.name }}
          </div>
          <div class="text-xs text-gray-600">
            {{ activeUserRole?.name }}
          </div>
        </div>
      </div>
    </div>
  </aside>

  <Menu

    ref="profileMenu"
    :model="computedMenuItems"
    :popup="true"
  >
    <template #item="{ item }">
      <div v-if="item.label === 'Logout'">
        <SignOutButton :redirect-url="`${authAppUrl}/auth/sign-in`">
          <button
            class="flex items-center space-x-2 p-2 cursor-pointer"
            @click="handleMenuItemClick(item), showSideBar(item)"
          >
            <Icon :name="item.icon || ''" />
            <span class="font-sans">{{ item.label }}</span>
          </button>
        </SignOutButton>
      </div>
      <div v-else>
        <a
          v-ripple
          class="flex items-center space-x-2 p-2 cursor-pointer"
          @click="handleMenuItemClick(item), showSideBar(item)"
        >
          <Icon :name="item.icon || ''" />
          <span class="font-sans">{{ item.label }}</span>
        </a>
      </div>
    </template>
  </Menu>

  <Popover
    v-if="workSpaces.length > 1"
    ref="op"
  >
    <div
      class="grid grid-cols-1 w-[12.5rem]"
    >
      <div
        v-for="(workSpace, index) in workSpaces.filter(ws => ws.WorkSpace.id !== workSpace.id)"
        :key="index"
        class="w-full py-2 px-1 relative hover:bg-gray-100 cursor-pointer"
        :class="isSettingWorkSpace ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'"
        @click="changeWorkSpace(workSpace)"
      >
        {{ workSpace?.WorkSpace.name }}

        <span class="absolute right-0">
          <Icon
            name="mdi-chevron-right"
            class="w-4 h-4 text-gray-500"
          />
        </span>
      </div>
      <div
        class="w-full relative hover:bg-gray-100 flex items-center py-2 px-1 cursor-pointer"
        @click="showCreateNewWorkSpace = true, op.hide()"
      >
        New WorkSpace
        <span class="absolute right-0">
          <Icon
            name="mdi-plus"
            class="w-4 h-4 text-gray-500"
          />
        </span>
      </div>
    </div>
  </Popover>

  <WorkspaceCreateNew
    v-model:visible="showCreateNewWorkSpace"
    :userName="loggedInUser?.name"
    @init="init"
  />

  <UserProfile
    v-model:activeUser="activeUser"
    v-model:roles="roles"
    v-model:showUserSidebarVisible="showUserSidebarVisible"
    v-model:isThisEditMode="isThisEditMode"
    v-model:hideStatus="hideStatus"
    @load-users="loadUsers"
  />

  <div
    v-if="isLoading"
    class="w-full fixed h-screen overflow-y-scroll z-50 bg-white flex items-center justify-center"
  >
    <ProgressSpinner />
  </div>

  <!-- Universal Search Dialog -->
  <UniversalSearch ref="searchDialogRef" />
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { useRoleStore } from '~/store/roleStore'
import { useUtilsStore } from '~/store/utils'
import UniversalSearch from '~/components/Widgets/UniversalSearch.vue'
import { useAskAiModal } from '~/composables/useAskAiModal'

const { user } = useUser()
const { open: openAskAi } = useAskAiModal()
const { isAuditor } = useRolePermissions()

const { roleId, roleName, roleCode } = storeToRefs(useRoleStore())
const toggleCollapse = () => {
  useUtilsStore().toggleSidebarOpen()
}

const keepSidebarOpenRoutes = [
  'index',
  'businesses',
  'pipeline',
  'contracts',
  'contacts',
  'tasks',
  'templates',
  'automations',
  'campaign',
  'campaign-create',
  'campaign-id',
  'calendar',
  'settings',
  'users',
  'billing',
]

const isKeepSidebarOpen = computed(() => {
  return keepSidebarOpenRoutes.includes(route.name as string)
})
const route = useRoute()

const toggleCollapseManual = (open: boolean) => {
  if (isCollapsed.value === false && open === true) {
    return
  }
  else if (isCollapsed.value === true && open === false) {
    return
  }

  //  if isCollapsed is false and  keepSidebarOpenRoutes includes the route name then keep the sidebar open
  else if (keepSidebarOpenRoutes.includes(route.name as string)) {
    //  if isCollapsed is true then open the sidebar
    if (isCollapsed.value === true) {
      useUtilsStore().toggleSidebarOpen()
    }
    else {
      return
    }
  }
  //  id keepSidebarOpenRoutes does not include the route name then allow toggle the sidebar
  else {
    useUtilsStore().toggleSidebarOpen()
  }
}

watch(route?.name, (newVal) => {
  if (keepSidebarOpenRoutes.includes(newVal as string)) {
    console.log('🚀~newVal:', newVal)
    toggleCollapseManual(true)
  }
  else {
    toggleCollapseManual(false)
  }
})

const isCollapsed = computed(() => {
  console.log('🚀~isCollapsed.value:', sidebarOpen.value)
  return sidebarOpen.value
})

const {
  profileMenu,
  loggedInUser,
  workSpace,
  menus,
  menuItems,
  MenuLabel,
  getInitials,
  isActive,
  navigateTo,
  toggleMenu,
  handleMenuItemClick,
} = useSidebar()
const showSideBar = (item: any) => {
  if (item.label === 'My Profile') {
    useRouter().push('/auth/profile')
  }
}
const authAppUrl = process.env.AUTH_APP_URL || 'https://auth.cimpro.app'

const checkIfSupport = computed(() => {
  return user.value?.publicMetadata?.isAdmin === true
})

const { sidebarOpen } = storeToRefs(useUtilsStore())

const loadUsers = async () => {
  const activeUserRes = await getUserWorkSpaceFromDB()
  activeUser.value = activeUserRes?.data
}
// GET WORKSPACE
const workSpaces = ref([])
const { getAllWorkSpaces, setActiveWorkSpace } = useWorkSpace()
const { getUserWorkspace: getUserWorkSpaceFromDB } = useUserWorkSpace()
const { getRoles: getRolesFromDB } = useUsers()
//
const showUserSidebarVisible = ref(false)
const isThisEditMode = ref(true)
const roles = ref([])
const activeUser = ref('')
const hideStatus = ref(true)
const activeUserRole = computed(() => {
  const activeRole = roles.value.find(role => role.id === activeUser.value.roleId)
  if (activeRole) {
    roleId.value = activeRole.id
    roleName.value = activeRole.name
    roleCode.value = activeRole.code
  }
  return activeRole
})

const computedMenuItems = computed(() => {
  const isSupport = checkIfSupport.value
  if (isSupport) {
    console.log('🚀~isSupport.value:', menuItems)

    const filteredMenuItems = menuItems?.filter(item => item.id !== MenuLabel.PROFILE)
    console.log('🚀~filteredMenuItems:', filteredMenuItems)
    //  dont have profile menu
    return filteredMenuItems
  }
  else {
    return menuItems
  }
})

const isLoading = ref(false)
const init = async () => {
  isLoading.value = true

  // Set active user
  try {
    const res = await getAllWorkSpaces()
    workSpaces.value = res?.data
    const activeUserRes = await getUserWorkSpaceFromDB()
    activeUser.value = activeUserRes?.data
  }
  catch (e) {
    // useRouter().push('/auth/sign-in')
    isLoading.value = false
    console.log('🚀~init.value:', isLoading.value)
  }

  // GET ROLES
  const resForRoles = await getRolesFromDB()
  roles.value = resForRoles.data
  isLoading.value = false
}
const isSettingWorkSpace = ref(false)
const changeWorkSpace = async (userWorkSpace: any) => {
  if (isSettingWorkSpace.value) {
    return
  }
  isSettingWorkSpace.value = true
  await setActiveWorkSpace({
    workSpaceId: userWorkSpace.WorkSpace.id,
  })
  await useRoleStore().fetchUserRole()
  isSettingWorkSpace.value = false
  // useRouter().push('/')
  //
  window.location.replace('/')
}
const showCreateNewWorkSpace = ref(false)
const op = ref()
const searchDialogRef = ref<InstanceType<typeof UniversalSearch> | null>(null)

const openSearch = () => {
  searchDialogRef.value?.showDialog()
}

// Keyboard shortcut: Cmd/Ctrl + K to open search
const handleKeyDown = (event: KeyboardEvent) => {
  if (isAuditor) return
  if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
    event.preventDefault()
    openSearch()
  }
}

onMounted(async () => {
  if (keepSidebarOpenRoutes.includes(route.name as string)) {
    toggleCollapseManual(true)
  }
  else {
    toggleCollapseManual(false)
  }
  await init()
  window.addEventListener('keydown', handleKeyDown)
})

onUnmounted(() => {
  window.removeEventListener('keydown', handleKeyDown)
})
</script>
